import { useEffect, useRef, useState } from "react";

let ytApiPromise = null;
const ytPlayers = new Map();
export const getYtPlayer = (iframeId) => ytPlayers.get(iframeId) || null;

function loadYouTubeAPI() {
  if (typeof window === "undefined") return Promise.resolve(null);
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (ytApiPromise) return ytApiPromise;
  ytApiPromise = new Promise((resolve) => {
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    document.head.appendChild(tag);
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof prev === "function") prev();
      resolve(window.YT);
    };
  });
  return ytApiPromise;
}

const isYouTube = (url) => /youtube\.com\/embed|youtu\.be/.test(url || "");

/** Append enablejsapi so the YouTube IFrame API can bind to an existing iframe. */
export const withJsApi = (url) => {
  if (!isYouTube(url)) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}enablejsapi=1&vq=hd1080&origin=${encodeURIComponent(window.location.origin)}`;
};

// YouTube treats this as a hint; it picks the best quality the viewer's bandwidth and player size allow.
const requestHd = (p) => {
  try {
    p?.setPlaybackQuality?.("hd1080");
  } catch (e) {
    /* noop */
  }
};

/** Fires onEnd when the embedded video completes, and periodically reports playback
 *  progress via onProgress(currentTime, duration).
 *  - YouTube: official IFrame Player API (state ENDED === 0) + getCurrentTime polling
 *  - Other (Internet Archive, etc.): postMessage 'ended' + a duration-timer fallback (no progress) */
export function useVideoEnd({ videoId, embedUrl, durationSec, iframeId, enabled, onEnd, onProgress, onError }) {
  const onEndRef = useRef(onEnd);
  onEndRef.current = onEnd;
  const onErrRef = useRef(onError);
  onErrRef.current = onError;
  const onProgRef = useRef(onProgress);
  onProgRef.current = onProgress;

  useEffect(() => {
    if (!enabled || !embedUrl) return undefined;
    const cleanups = [];

    if (isYouTube(embedUrl)) {
      let cancelled = false;
      let player = null;
      loadYouTubeAPI().then((YT) => {
        if (cancelled || !YT) return;
        const el = document.getElementById(iframeId);
        if (!el) return;
        try {
          player = new YT.Player(iframeId, {
            events: {
              onReady: (e) => requestHd(e.target),
              onStateChange: (e) => {
                if (e.data === 1 || e.data === 3) requestHd(e.target);
                if (e.data === 0) onEndRef.current && onEndRef.current();
              },
              onError: (e) => onErrRef.current && onErrRef.current(e.data),
            },
          });
          ytPlayers.set(iframeId, player);
        } catch (err) {
          /* noop */
        }
      });
      const iv = setInterval(() => {
        try {
          if (player && player.getCurrentTime && player.getDuration) {
            const ct = player.getCurrentTime();
            const du = player.getDuration();
            if (du > 0 && onProgRef.current) onProgRef.current(ct, du);
          }
        } catch (e) {
          /* noop */
        }
      }, 5000);
      cleanups.push(() => {
        cancelled = true;
        clearInterval(iv);
        if (ytPlayers.get(iframeId) === player) ytPlayers.delete(iframeId);
        try {
          player && player.destroy && player.destroy();
        } catch (e) {
          /* noop */
        }
      });
    } else {
      const onMsg = (e) => {
        const d = e.data;
        const ended =
          d === "ended" ||
          (d && d.event === "ended") ||
          (typeof d === "string" && d.toLowerCase().includes("ended"));
        if (ended) onEndRef.current && onEndRef.current();
      };
      window.addEventListener("message", onMsg);
      cleanups.push(() => window.removeEventListener("message", onMsg));

      if (durationSec && durationSec > 0) {
        const t = setTimeout(() => onEndRef.current && onEndRef.current(), (durationSec + 3) * 1000);
        cleanups.push(() => clearTimeout(t));
      }
    }

    return () => cleanups.forEach((fn) => fn());
  }, [videoId, embedUrl, durationSec, iframeId, enabled]);
}

/** Persisted autoplay-next preference (on by default). */
export function useAutoplayPref() {
  const [on, setOn] = useState(() =>
    typeof window !== "undefined" ? localStorage.getItem("reelvault_autoplay") !== "off" : true
  );
  const toggle = () =>
    setOn((prev) => {
      const next = !prev;
      localStorage.setItem("reelvault_autoplay", next ? "on" : "off");
      return next;
    });
  return [on, toggle];
}
